import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import PlatformAiChat from '@/modules/chat/components/PlatformAiChat';
import { ChatConfig, Message } from '@/modules/chat/types/types';

export class InnovaChatElement extends HTMLElement {
  private root: ReturnType<typeof createRoot> | null = null;
  private _llmConfigId?: number;
  private _authInstanceId?: number;
  private _backendUrl?: string;
  private _collections?: number[];
  private _jwt?: string;
  private _apiIds?: number[];
  private _dynamicParams?: ChatConfig['dynamicParams'];
  private _promptSuggestions?: string[];
  private _messages?: Message[];
  private _primaryColor?: string;
  private _secondaryColor?: string;
  private _chatButtonIcon?: string;
  private _aiMessageIcon?: string;
  private _loadingLottieUrl?: string;
  private _statusMessages?: string[];
  private _statusInterval?: number;
  private _statusDuration?: number;

  static get observedAttributes() {
    return [
      'llm-config-id',
      'auth-instance-id',
      'backend-url',
      'collections',
      'api-ids',
      'dynamic-params',
      'prompt-suggestions',
      'messages',
      'jwt',
      'primary-color',
      'secondary-color',
      'chat-button-icon',
      'ai-message-icon',
      'loading-lottie-url',
      'status-messages',
      'status-interval',
      'status-duration',
    ];
  }

  connectedCallback() {
    // Create a shadow root
    const shadowRoot = this.attachShadow({ mode: 'open' });

    // Create a container for React
    const container = document.createElement('div');
    shadowRoot.appendChild(container);

    // Create a style element to inject styles
    const style = document.createElement('style');
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `;
    shadowRoot.appendChild(style);

    // Mount React component to shadow DOM
    this.root = createRoot(container);
    this.renderComponent();
  }

  disconnectedCallback() {
    // Clean up React component when element is removed
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (newValue === oldValue) return;

    switch (name) {
      case 'llm-config-id':
        this._llmConfigId = parseInt(newValue, 10) || 1;
        break;
      case 'auth-instance-id':
        this._authInstanceId = parseInt(newValue, 10) || 1;
        break;

      case 'backend-url':
        this._backendUrl = newValue;
        break;
      case 'jwt':
        this._jwt = newValue;
        break;
      case 'collections':
      case 'api-ids':
      case 'prompt-suggestions':
      case 'status-messages':
        try {
          const parsedValue = JSON.parse(newValue);
          if (name === 'collections') this._collections = parsedValue;
          if (name === 'api-ids') this._apiIds = parsedValue;
          if (name === 'prompt-suggestions') this._promptSuggestions = parsedValue;
          if (name === 'status-messages') this._statusMessages = parsedValue;
        } catch (e) {
          console.error(`Failed to parse attribute ${name}:`, e);
        }
        break;
      case 'status-interval':
        this._statusInterval = parseInt(newValue, 10);
        break;
      case 'status-duration':
        this._statusDuration = parseInt(newValue, 10);
        break;
      case 'dynamic-params':
      case 'messages':
        try {
          const parsedValue = JSON.parse(newValue);
          if (name === 'dynamic-params') this._dynamicParams = parsedValue;
          if (name === 'messages') this._messages = parsedValue;
        } catch (e) {
          console.error(`Failed to parse attribute ${name}:`, e);
        }
        break;
      case 'primary-color':
        this._primaryColor = newValue;
        break;
      case 'secondary-color':
        this._secondaryColor = newValue;
        break;
      case 'chat-button-icon':
        this._chatButtonIcon = newValue;
        break;
      case 'ai-message-icon':
        this._aiMessageIcon = newValue;
        break;
      case 'loading-lottie-url':
        this._loadingLottieUrl = newValue;
        break;
    }

    this.renderComponent();
  }

  renderComponent() {
    if (
      this.root &&
      this._llmConfigId !== undefined &&
      this._backendUrl !== undefined &&
      this._authInstanceId !== undefined
    ) {
      this.root.render(
        createElement(PlatformAiChat, {
          props: {
            jwt: this._jwt,
            llmConfigId: this._llmConfigId,
            authInstanceId: this._authInstanceId,
            backendUrl: this._backendUrl,
            collections: this._collections,
            apiIds: this._apiIds,
            dynamicParams: this._dynamicParams,
            promptSuggestions: this._promptSuggestions,
            messages: this._messages,
            primaryColor: this._primaryColor,
            secondaryColor: this._secondaryColor,
            chatButtonIcon: this._chatButtonIcon,
            aiMessageIcon: this._aiMessageIcon,
            loadingLottieUrl: this._loadingLottieUrl,
            statusMessages: this._statusMessages,
            statusInterval: this._statusInterval,
            statusDuration: this._statusDuration,
          },
        })
      );
    }
  }
}

// Define custom element
customElements.define('innova-chat', InnovaChatElement);
